/// @ts-check
import partnerResponseService from '../services/partnerResponseService';
import toastManager from './toastManager';
import { GroupContextTypes } from '../types/groupContext.types.js';

/**
 * @typedef {import('../types/groupContext.types.js').GroupContext} GroupContext
 * @typedef {import('../types/groupContext.types.js').GroupContextMode} GroupContextMode
 */

/**
 * @typedef {Object} ActivityCoreData
 * @property {string} [activityDescription] - 活動描述
 * @property {string} [activityTitle] - 活動標題
 * @property {Array<{title: string, content: string}>} [initialNodes] - 引導單元
 */

/**
 * @typedef {Object} TriggerPartnerResponseParams
 * @property {string} userNodeId - 用戶節點 ID
 * @property {string} title - 消息標題
 * @property {string} content - 消息內容
 * @property {string} author - 作者名稱
 * @property {string} activityId - 活動 ID
 * @property {string | number} groupId - 群組 ID
 * @property {GroupContextMode} mode - 模式
 * @property {GroupContext} groupContext - 小組上下文
 * @property {string[]} [partnerIds=[]] - 指定的夥伴 ID 列表
 * @property {string} [parentTag] - 父標籤
 * @property {ActivityCoreData | null} [activityCoreData] - 活動核心資料
 */

/**
 * @typedef {Object} TriggerResult
 * @property {boolean} success - 是否成功
 * @property {string} [reason] - 失敗原因
 * @property {number} [responseCount] - 回應數量
 * @property {string} [message] - 消息
 * @property {any} [error] - 錯誤信息
 */

/**
 * 夥伴回應觸發器（精簡版）
 * 後端直接創建節點並推播到 Socket.IO，前端只負責調用 API
 */
export class PartnerResponseTrigger {
  constructor() {
    this.processingQueue = new Set();
    this.requestCounter = 0;
  }

  /**
   * 觸發夥伴回應流程
   * @param {TriggerPartnerResponseParams} params - 參數對象
   * @returns {Promise<TriggerResult>} 處理結果
   */
  async triggerPartnerResponse({
    userNodeId,
    title,
    content,
    author,
    activityId,
    groupId,
    mode,
    groupContext,
    partnerIds = [],
    parentTag,
    activityCoreData = null
  }) {
    const requestId = `${Date.now()}_${++this.requestCounter}`;
    
    if (this.processingQueue.size >= 5) {
      console.warn('[Partner Response] 並發請求過多（>5），跳過');
      return { success: false, reason: 'too_many_concurrent_requests' };
    }

    try {
      this.processingQueue.add(requestId);
      
      if (!activityId || !groupId) {
        console.error('[Partner Response] 缺少 activityId 或 groupId');
        return { success: false, reason: 'missing_required_params' };
      }
      
      // 強類型驗證 groupContext
      if (!GroupContextTypes.isGroupContext(groupContext)) {
        console.error('[Partner Response] groupContext 類型無效', groupContext);
        return { success: false, reason: 'invalid_group_context_type' };
      }
      
      if (!groupContext.conversationHistory || !Array.isArray(groupContext.conversationHistory)) {
        console.error('[Partner Response] conversationHistory 無效');
        return { success: false, reason: 'invalid_conversation_history' };
      }

      console.log(`[Partner Response] 觸發 ${mode.toUpperCase()} 模式:`, {
        historyLength: groupContext.conversationHistory.length,
        partners: partnerIds.length || '全部'
      });

      // 將 parentTag 加入 groupContext
      const enrichedContext = { ...groupContext, parentTag };

      // 調用後端 API（後端會直接創建節點並推播）
      const result = await partnerResponseService.handleUserMessage(
        activityId,
        groupId.toString(),
        {
          id: userNodeId,
          content: `${title}\n\n${content}`,
          author: author,
          timestamp: new Date().toISOString()
        },
        partnerIds,
        enrichedContext,
        activityCoreData
      );

      if (!result?.responses?.length) {
        return { success: true, responseCount: 0, message: 'no_partner_responses' };
      }

      const respondedCount = result.responses.filter(r => r.responded).length;
      
      if (respondedCount > 0) {
        toastManager.success(`${respondedCount} 個夥伴正在回應`, 3000);
      }

      return {
        success: true,
        responseCount: respondedCount,
        message: `${respondedCount} 個夥伴已回應`
      };

    } catch (error) {
      console.error(`[Partner Response] 請求 ${requestId} 失敗:`, error);
      return { success: false, reason: 'trigger_failed', error: error.message };
    } finally {
      this.processingQueue.delete(requestId);
    }
  }
}

// 導出單例
const partnerResponseTrigger = new PartnerResponseTrigger();

export const triggerPartnerResponse = (params) => {
  return partnerResponseTrigger.triggerPartnerResponse(params);
};

export default partnerResponseTrigger;
